import React, { useContext } from 'react';
import { T } from '@sm/intl';
import { OpenEndedAnswerType, RespondentCapabilityProvider } from '@sm/question-widgets/respondent-survey';
import { ExternalQuestion } from '@sm/question-definitions';
import { BypassBlock } from '~app/components/core/BypassBlock';
import { OQAATProvider, ScrollAnchor } from '~app/components/Survey/OneQuestionAtATime';
import { ExitLink } from '~app/components/Survey/ExitLink';
import { SurveyTitle } from '~app/components/Survey/SurveyTitleV2';
import createHeadingLevel from './helper/createHeadingLevel';
import SurveyFooter from '~app/components/Survey/FooterContainer';
import { PageTitle } from '~app/components/Survey/PageTitle';
import { PageDescription } from '~app/components/Survey/PageDescription';
import { SurveyButton } from '~app/components/Survey/SurveyButton';
import { Layout } from './components/Layout';
import { QuestionList } from './components/QuestionList';
import { PersistentProgressBar } from '~app/components/Survey/PersistentProgressBar';
import extrapolateSurveyData from './helper/extrapolateSurveyData';
import useStyles from './useStyles';
import { SurveyContext } from './SurveyContext';
import { CurrentPageContext } from './CurrentPageContext';
import { Logo } from './components/Logo';
import useCenterContainerOffset from './hooks/useCenterContainerOffset';

const COPY = {
  SKIP_LINK_CONTENT: {
    id: 'SurveyTaking.SkipLinkContent',
    defaultMessage: 'Skip to content',
    description: '[Type: label][Vis: med] - Skip to content link label (Accessibiility)',
  },
};

const Survey: React.FC = () => {
  const { survey, activePage } = useContext(SurveyContext);
  const { questions = [], answers = {}, updateAnswers } = useContext(CurrentPageContext);

  const {
    isSurveyTitleEnabled,
    showPageTitles,
    langCode,
    isFooterEnabled,
    isAutoScroll,
    totalQuestionsCount,
    isQuestionNumberingEnabled,
    showRequiredQuestionAsterisks,
    logo: { enabled: isLogoEnabled, ...logoProps },
    surveyTitleAlignment,
    themeVersion,
  } = extrapolateSurveyData(survey);

  // TODO: Refactor isRTL to use provider
  const isRTL = ['ar', 'he', 'fa'].includes(langCode || '');
  const surveyTitleHTML = survey?.titleHTML;

  const surveyId = survey?.id;
  /**
   * Page Header
   * calculate proper Page Title and Description based on current page
   */
  const hasPageDescription = !!activePage?.subHeading;
  const pageDescription = activePage?.subHeading;
  const pageTitle = activePage?.heading;
  /**
   * Skip to content will attempt to move the focus position (without actually focusing)
   * to the Survey Title (if exists), then Page title (if exists), finially to the
   * main-content container (<main>).
   * `tabIndex={-1}` is added to each element (backwards compat with <= IE 11)
   */
  const isExitLinkEnabled = true;
  const isProgressBarEnabled = true;
  const surveyTitleId = `survey-title-${surveyId}`;
  const pageTitleId = `page-title-${surveyId}`;
  const mainContentId = `main-content-${surveyId}`;

  let skipContentId = mainContentId;
  if (showPageTitles) {
    skipContentId = pageTitleId;
  }
  const nextHeadingElement = createHeadingLevel();

  const layoutContainerRef = React.useRef<HTMLDivElement>(null);
  const pageTitleRef = React.useRef<HTMLDivElement>(null);
  const persistentProgressRef = React.useRef<HTMLDivElement>(null);
  const partialContainerRef = React.useRef<HTMLDivElement>(null);
  const logoContainerRef = React.useRef<HTMLDivElement>(null);
  const toolbarContainerRef = React.useRef<HTMLDivElement>(null);
  const topContainerRef = React.useRef<HTMLDivElement>(null);
  const mainContainerRef = React.useRef<HTMLDivElement>(null);
  const mainRef = React.useRef<HTMLDivElement>(null);

  const offsetTop = useCenterContainerOffset(
    pageTitleRef,
    [persistentProgressRef?.current?.offsetHeight || 0],
    [topContainerRef?.current?.offsetHeight || 0],
    (margin: number) => {
      if (themeVersion === 'V3') {
        const cr = window.getComputedStyle(mainContainerRef?.current as Element);
        return Math.abs(margin / 2 - parseInt(cr.paddingTop, 10));
      }
      return margin;
    }
  );

  const {
    surveyTitleContainer,
    pageHeaderContainer,
    layoutMainContainer,
    questionContainer,
    pageFooterContainer,
    pageNavigationContainer,
    navButtons,
  } = useStyles({ offsetTop });

  const layoutContainerOptions = {
    layoutContainer: { ref: layoutContainerRef },
    partialContainer: { ref: partialContainerRef },
    topContainer: { ref: topContainerRef },
    mainContainer: { className: layoutMainContainer, ref: mainContainerRef },
    logoContainer: { ref: logoContainerRef },
    toolbarContainer: { ref: toolbarContainerRef },
  };

  const settings = {
    survey: {
      numberingEnabled: isQuestionNumberingEnabled,
      asteriskEnabled: showRequiredQuestionAsterisks,
    },
  };

  const upstreamActions = {
    onLeaveQuestion: (externalQuestion: ExternalQuestion, answer: OpenEndedAnswerType) => {
      if (questions) {
        updateAnswers(answer);
      }
    },
  };

  return (
    <OQAATProvider>
      <BypassBlock>
        <a href={`#${skipContentId}`}>
          <T desc={COPY.SKIP_LINK_CONTENT} />
        </a>
      </BypassBlock>
      <Layout
        isRTL={isRTL}
        logo={isLogoEnabled && <Logo {...logoProps} />}
        tools={isExitLinkEnabled && <ExitLink href="#">Exit</ExitLink>}
        options={layoutContainerOptions}
      >
        <main id={mainContentId} ref={mainRef}>
          <div id={`survey-title-container-${surveyId}`} className={surveyTitleContainer}>
            <If condition={isSurveyTitleEnabled}>
              <SurveyTitle
                id={surveyTitleId}
                align={surveyTitleAlignment}
                element={nextHeadingElement(0)}
                text={surveyTitleHTML || ''}
              />
            </If>
            {/* <SurveyIntro text={surveyIntro} /> */}
            {/* <OKButton aria-label="Continue" /> */}
          </div>

          <header id={`page-header-container-${surveyId}`} className={pageHeaderContainer}>
            <If condition={showPageTitles}>
              <PageTitle id={pageTitleId} element={nextHeadingElement(1)} text={pageTitle || ''} tabIndex={-1} />
            </If>
            <ScrollAnchor ref={pageTitleRef} noShield>
              {
                (({ nextQuestion }: any) => (
                  <>
                    <If condition={hasPageDescription}>
                      <PageDescription text={pageDescription || ''} />
                    </If>
                    <SurveyButton size="md" onClick={nextQuestion}>
                      OK
                    </SurveyButton>
                  </>
                  // TODO: remove 'any' when jsx-control-statements are removed
                )) as any
              }
            </ScrollAnchor>
          </header>

          <RespondentCapabilityProvider settings={settings} upstreamActions={upstreamActions}>
            <form id={`form-${surveyId}`} className={questionContainer}>
              {/**
               * NOTE: active (boolean) will be controlled by Progression Engine
               * NOTE: Are all the nullified fields (questionBank, validation) needed?
               */}
              <QuestionList
                answers={answers}
                questions={questions.map(
                  q =>
                    ({
                      ...q,
                      active: true,
                      questionBank: null,
                      validation: null,
                      errors: [],
                      disabled: false,
                      questionNumber: q.position.inSurvey,
                      title: q.heading.default.text,
                      answer: {
                        touched: false,
                        value: '',
                      },
                      typeId: 'single_textbox',
                      // Something here is broken/incompatible at the moment
                    } as unknown as ExternalQuestion)
                )}
              />
              {/**
               *  When survey is in OQAAT mode, progress bar is below the survey footer and is sticky
               */}
              <If condition={!!isProgressBarEnabled && !!isAutoScroll}>
                <PersistentProgressBar value={1} total={totalQuestionsCount} ref={persistentProgressRef} />
              </If>
              <div className={pageNavigationContainer}>
                <SurveyButton className={navButtons} size="lg">
                  GO BACK A PAGE
                </SurveyButton>
                <SurveyButton className={navButtons} size="lg" data-testid="Survey__DoneButton">
                  DONE
                </SurveyButton>
                <SurveyButton className={navButtons} size="lg">
                  GO FORWARD A PAGE
                </SurveyButton>
              </div>
            </form>
          </RespondentCapabilityProvider>
          <footer id={`page-footer-container-${surveyId}`} className={pageFooterContainer}>
            PAGE FOOTER
          </footer>

          <If condition={!!isFooterEnabled}>
            <SurveyFooter
              id={`survey-footer-container-${surveyId}`}
              isGrayScale={!!survey?.design?.footer?.grayscale}
              showPrivacyCookiePolicy
              // Below are placeholders, fill in when we get info from query
              surveyMonkeyURL=""
              howItWorksURL=""
              privacyURL=""
              cookiePolicyURL=""
              showReportProblem={false}
              reportProblemURL=""
              isGoogleSubdomain={false}
              googlePrivacyPolicyURL=""
            />
          </If>
        </main>
      </Layout>
    </OQAATProvider>
  );
};

export default Survey;
